import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  Spell,
  SpellLimitation, 
  UpgradableNumber, 
} from "@dndModels";
import { 
  AREA_TYPES,
  ATTACK_TYPES, 
  DAMAGE_TYPES, 
  DURATION_OPTIONS, 
  EFFECT_TYPES, 
  FEATURE_SOURCE_TYPES, 
  FEATURE_SOURCE_TYPE_OPTIONS, 
  SCHOOLS,
  SPELL_LEVELS,
  STANDARD_CASTING_TIMES,
  TARGET_TYPES
} from "@dndModels/Constants";
import { 
  AreaType, 
  AttackType, 
  CastingTime, 
  DamageType, 
  DataType, 
  EffectType, 
  SpellLevel, 
  TargetType
} from "@dndModels/Types";
import { UpsertableBase } from "@models";

import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import SelectSpellIndex from "@dndComponents/select/SelectSpellIndex";
import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DisplayStringWithReplacements from '@dndComponents/display/DisplayStringWithReplacements';

interface Props { 
  obj: SpellLimitation;
  change_time?: number;
  onChange: (changed: SpellLimitation) => void;
  onDelete?: () => void;
  disabled?: boolean;
  block_spell_lists?: string[];
  attached_table?: AttachedTable;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SpellLimitInput({ 
  obj,
  change_time = 0,
  onChange,
  onDelete,
  disabled = false, 
  block_spell_lists = [],
  attached_table
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [filtered, setFiltered] = useState<Spell[]>([]);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [reloading, setReloading] = useState(false);
  const [refiltering, setRefiltering] = useState(false);
  const [mode, setMode] = useState("Main");
  const [spell_limit_text, setSpellLimitText] = useState("");
  const [parse_string, setParseString] = useState("");
  const [copy_feedback, setCopyFeedback] = useState(false);

  const load = useCallback(async () => {
    setFetched(true);
    setLoading(true);
    const res = await api.getObjects({ data_type: "spell" });
    setSpells(res as Spell[]);
    setLoading(false);
    setRefiltering(true);
  }, []);

  useEffect(() => {
    if (!fetched) {
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (refiltering) {
      setRefiltering(false);
      updateFiltered();
    }// eslint-disable-next-line
  }, [refiltering, setRefiltering]);

  useEffect(() => {
    if (prev_change_time !== change_time) {
      setChangeTime(change_time);
    }
  }, [prev_change_time, change_time, setChangeTime]);

  const renderDurations = () => {
    const min_duration_num = obj.min_duration === null ? -2 : obj.min_duration;
    const max_duration_num = obj.max_duration === null ? -2 : obj.max_duration;
    const min_durations = max_duration_num < 0 ? DURATION_OPTIONS.filter(o => o.id !== "-1") : DURATION_OPTIONS.filter(o => {
      const numeric = +o.id;
      return numeric !== -1 && numeric <= max_duration_num;
    });
    const max_durations = min_duration_num < 0 ? DURATION_OPTIONS.filter(o => o.id !== "-1") : DURATION_OPTIONS.filter(o => {
      const numeric = +o.id;
      return numeric !== -1 && numeric >= min_duration_num;
    });
    return (
      <>
        <Grid size={{ xs: 6 }}>
          <SelectStringBox 
            name="Min Duration" 
            options={[...min_durations, { id: "-2", text: "None" }]}
            value={obj.min_duration === null ? "-2" : `${obj.min_duration}`} 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.min_duration = changed === "-2" ? null : +changed;
              onChange(new_obj);
            }} 
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <SelectStringBox 
            name="Max Duration" 
            options={[...max_durations, { id: "-2", text: "None" }]}
            value={obj.max_duration === null ? "-2" : `${obj.max_duration}`} 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.max_duration = changed === "-2" ? null : +changed;
              onChange(new_obj);
            }} 
          />
        </Grid>
      </>
    );
  }
  
  const renderRanges = () => {
    return (
      <>
        <Grid size={{ xs: 3 }}>
          <CheckBox
            name="Use Min Range"
            value={obj.min_range !== null}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.min_range = changed ? 0 : null;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          {obj.min_range !== null &&
            <StringBox 
              name="Min Range" 
              value={`${obj.min_range}`} 
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.min_range = +changed;
                onChange(new_obj);
              }} 
            />
          }
        </Grid>
        <Grid size={{ xs: 3 }}>
          <CheckBox
            name="Use Max Range"
            value={obj.max_range !== null}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.max_range = changed ? 0 : null;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          {obj.max_range !== null &&
            <StringBox 
              name="Max Range" 
              value={`${obj.max_range}`} 
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.max_range = +changed;
                onChange(new_obj);
              }} 
            />
          }
        </Grid>
        <Grid size={{ xs: 6 }}>
          <SelectStringBox
            name="Touch"
            value={obj.touch}
            options={["Ignore", "No", "Yes"]}
            onChange={(changed: "Yes" | "No" | "Ignore") => {
              const new_obj = obj;
              new_obj.touch = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <SelectStringBox
            name="Self"
            value={obj.self}
            options={["Ignore", "No", "Yes"]}
            onChange={(changed: "Yes" | "No" | "Ignore") => {
              const new_obj = obj;
              new_obj.self = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
      </>
    );
  }

  const handleMode = async (changed: string) => {
    setMode(changed);
    if (changed === "Show Spells") {
      setLoading(true);
      setSpellLimitText(await api.getSpellLimitText(obj));
      setTimeout(() => {
        setLoading(false);
      }, 100);
    } 
  }

  const updateFiltered = () => {
    setFiltered(spells.filter(o => api.fitSpellLimit({ spell: o, limit: obj, ignore_variance: true })));
  }

  const renderTools = () => {
    return (
      <>
        <Grid size={6}>
          <ButtonBox
            name={copy_feedback ? "Copied!" : "Copy Limit JSON"}
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(obj.toDBObj(), null, 2))
                .then(() => {
                  setCopyFeedback(true);
                  setTimeout(() => setCopyFeedback(false), 2000);
                })
                .catch(err => console.error("Clipboard write failed", err));
            }}
          />
        </Grid>
        <Grid size={6}>
          <ButtonBox
            name="Paste Limit JSON"
            disabled={disabled}
            onClick={() => {
              navigator.clipboard.readText()
                .then(text => {
                  try {
                    const parsed = JSON.parse(text);
                    const new_limit = new SpellLimitation(parsed);
                    onChange(new_limit);
                    setRefiltering(true);
                  } catch (err) {
                    console.error("Failed to parse clipboard JSON as SpellLimitation", err);
                  }
                })
                .catch(err => console.error("Clipboard read failed", err));
            }}
          />
        </Grid>
        <Grid size={10}>
          <StringBox
            name="Parse String"
            value={parse_string}
            multiline
            onBlur={(changed: string) => {
              while (changed.includes(" No ")) {
                changed = changed.replace(" No ", " No\n");
              }
              while (changed.includes(" Yes ")) {
                changed = changed.replace(" Yes ", " Yes\n");
              }
              while (changed.includes("\u2019")) {
                changed = changed.replace("\u2019", "'");
              }
              setParseString(changed);
            }}
          />
        </Grid>
        <Grid size={2}>
          <ButtonBox
            name="Parse"
            disabled={disabled}
            onClick={() => {
              const pieces = parse_string.split("\n");
              const new_obj = obj;
              for (const piece of pieces) {
                let spell_name = piece;
                spell_name = common_util.replaceAll(spell_name, "\u2019", "'");
                spell_name = common_util.replaceAll(spell_name, "*", "");
                spell_name = common_util.replaceAll(spell_name, "(ritual)", "");
                spell_name = spell_name.trim().toLowerCase();
                const spell_finder = spells.filter(o =>
                  o.name.trim().toLowerCase() === spell_name && o.isDominant()
                );
                if (spell_finder.length === 1) {
                  const spell_id = spell_finder[0]._id;
                  if (!new_obj.spell_ids.includes(spell_id)) {
                    new_obj.spell_ids.push(spell_id);
                  }
                } else if (spell_finder.length > 1) {
                  console.error("Multiple spells found for:", spell_name, spell_finder);
                } else if (
                  spell_name.includes("cantrip") ||
                  spell_name.includes("evel") ||
                  spell_name.length === 0
                ) {
                  // Ignore header/section lines
                } else {
                  console.error("Unable to find spell when parsing list:", piece);
                }
              }
              onChange(new_obj.clone());
              setRefiltering(true);
            }}
          />
        </Grid>
      </>
    );
  }
  
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={{ xs: 12 }}>
          <CenteredMenu
            options={["Main", "Specific Included Spells", "Specific Excluded Spells", "Show Spells", "Tools"]}
            selected={mode}
            onSelect={handleMode}
          />
        </Grid>
        {!obj.use_selectable && !obj.list_ids.includes("Selectable") && !obj.list_ids.includes("Char Spell Lists") && 
          <Grid size={{ xs: 12 }}>
            Spell Count: {filtered.length}
          </Grid>
        }
        {mode === "Main" ?
          <>
            <Grid size={{ xs: obj.use_selectable ? 6 : 12 }}>
              <CheckBox
                name="Use Selectable Spell List" 
                value={obj.use_selectable} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.use_selectable = changed;
                  new_obj.selectable_key = "";
                  onChange(new_obj);
                }} 
              />
            </Grid>
            {obj.use_selectable ? 
              <Grid size={{ xs: 6 }}>
                <StringBox
                  name="Selectable Key"
                  value={obj.selectable_key}
                  onBlur={(changed: string) => {
                    const new_obj = obj;
                    new_obj.selectable_key = changed;
                    onChange(new_obj);
                  }}
                />
              </Grid>
            :
              <>
                <Grid size={{ xs: obj.list_ids.includes("Selectable") ? 3 : 6 }}>
                  <SelectObjectBox
                    name="Spell List"
                    data_type="spell_list"
                    extra_options={["Any", "Selectable", "Char Spell Lists"]}
                    values={obj.list_ids} 
                    multiple
                    disabled={disabled}
                    extra_filter={block_spell_lists.length > 0 ? (o: UpsertableBase) => {
                      return !block_spell_lists.includes(o._id);
                    } : undefined}
                    onChange={(changed: string[]) => {
                      const new_obj = obj;
                      if (changed.includes("Any")) {
                        new_obj.list_ids = ["Any"];
                      } else {
                        new_obj.list_ids = changed;
                      }
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                {obj.list_ids.includes("Selectable") && 
                  <Grid size={{ xs: 3 }}>
                    <StringBox
                      name="Selectable Key"
                      value={obj.spell_list_selectable_key} 
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        new_obj.spell_list_selectable_key = changed;
                        onChange(new_obj);
                      }} 
                    />
                  </Grid>
                }
                <Grid size={{ xs: obj.schools.includes("Selectable") ? 3 : 6 }}>
                  <SelectStringBox 
                    name="Schools" 
                    options={SCHOOLS}
                    extra_options={["Selectable"]}
                    values={obj.schools} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: string[]) => {
                      const new_obj = obj;
                      new_obj.schools = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                {obj.schools.includes("Selectable") && 
                  <Grid size={{ xs: 3 }}>
                    <StringBox
                      name="Selectable Key"
                      value={obj.school_selectable_key}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        new_obj.school_selectable_key = changed;
                        onChange(new_obj);
                      }} 
                    />
                  </Grid>
                }
                <Grid size={{ xs: obj.levels.includes(-1) ? 3 : 6 }}>
                  <SelectStringBox 
                    name="Levels" 
                    options={SPELL_LEVELS}
                    extra_options={["Use Min/Max"]}
                    values={obj.levels.map(o => { 
                      if (o === 0) return "Cantrip";
                      else if (o === -1) return "Use Min/Max";
                      else return `${o}`;
                    })} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: string[]) => {
                      const new_obj = obj;
                      new_obj.levels = changed.map(o => {
                        if (o === "Cantrip") return 0;
                        else if (o === "Use Min/Max") return -1;
                        else return +o;
                      }) as SpellLevel[];
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                {obj.levels.includes(-1) &&
                  <Grid container spacing={0} direction="row" size={{ xs: 3 }}>
                    <Grid size={{ xs: 6 }}>
                      <UpgradableNumberBox
                        name="Minimum Level"
                        value={obj.min_level} 
                        disabled={disabled}
                        attached_table={attached_table}
                        onChange={(changed: UpgradableNumber) => {
                          const new_obj = obj;
                          new_obj.min_level = changed.clone();
                          onChange(new_obj);
                          setRefiltering(true);
                        }} 
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <UpgradableNumberBox
                        name="Maximum Level"
                        value={obj.max_level} 
                        disabled={disabled}
                        attached_table={attached_table}
                        onChange={(changed: UpgradableNumber) => {
                          const new_obj = obj;
                          new_obj.max_level = changed.clone();
                          onChange(new_obj);
                          setRefiltering(true);
                        }} 
                      />
                    </Grid>
                  </Grid>
                }
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox 
                    name="Casting Times" 
                    options={STANDARD_CASTING_TIMES}
                    extra_options={["Special"]}
                    values={obj.casting_times} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: CastingTime[]) => {
                      const new_obj = obj;
                      new_obj.casting_times = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox 
                    name="Target Types" 
                    options={TARGET_TYPES}
                    values={obj.target_types} 
                    multiple
                    disabled={disabled}
                    onChange={(values: TargetType[]) => {
                      const new_obj = obj;
                      new_obj.target_types = values;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox 
                    name="Area Types" 
                    options={AREA_TYPES}
                    values={obj.area_types} 
                    multiple
                    disabled={disabled}
                    onChange={(values: AreaType[]) => {
                      const new_obj = obj;
                      new_obj.area_types = values;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox 
                    name="Attack Types" 
                    options={ATTACK_TYPES}
                    values={obj.attack_types} 
                    multiple
                    disabled={disabled}
                    onChange={(values: AttackType[]) => {
                      const new_obj = obj;
                      new_obj.attack_types = values;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox 
                    name="Effect Types" 
                    options={EFFECT_TYPES}
                    values={obj.effect_types} 
                    multiple
                    disabled={disabled}
                    onChange={(values: EffectType[]) => {
                      const new_obj = obj;
                      new_obj.effect_types = values;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Grid container spacing={0} direction="row">
                    <Grid size={{ xs: 12 }}>
                      <SelectStringBox 
                        name="Damage Types" 
                        options={DAMAGE_TYPES}
                        extra_options={["Selectable"]}
                        values={obj.damage_types} 
                        multiple
                        disabled={disabled}
                        onChange={(values: (DamageType | "Selectable")[]) => {
                          const new_obj = obj;
                          new_obj.damage_types = values;
                          onChange(new_obj);
                          setRefiltering(true);
                        }} 
                      />
                    </Grid>
                  </Grid>
                  {obj.damage_types.includes("Selectable") && 
                    <Grid size={{ xs: 12 }}>
                      <StringBox
                        name="Selectable Key"
                        value={obj.damage_selectable_key}
                        onBlur={(changed: string) => {
                          const new_obj = obj;
                          new_obj.damage_selectable_key = changed;
                          onChange(new_obj);
                        }}
                      />
                    </Grid>
                  }
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox 
                    name="Ritual" 
                    options={["Yes", "No", "Ignore"]}
                    value={obj.ritual} 
                    disabled={disabled}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.ritual = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox 
                    name="Concentration" 
                    options={["Yes", "No", "Ignore"]}
                    value={obj.concentration} 
                    disabled={disabled}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.concentration = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox
                    name="Twinnable"
                    value={obj.twinnable_1dd_ua7}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.twinnable_1dd_ua7 = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox
                    name="Disease"
                    value={obj.disease}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.disease = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Charm"
                    value={obj.charm}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.charm = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Contaminated"
                    value={obj.contaminated}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.contaminated = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Sight"
                    value={obj.sight}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.sight = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Verbal"
                    value={obj.verbal}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.verbal = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Somatic"
                    value={obj.somatic}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.somatic = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectStringBox
                    name="Material"
                    value={obj.material}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.material = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox
                    name="Material Cost Listed"
                    value={obj.m_cost_listed}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.m_cost_listed = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox
                    name="Material Consumed"
                    value={obj.m_consumed}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.m_consumed = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
                {obj.casting_times.includes("Special") &&
                  <Grid size={{ xs: 12 }}>
                    <StringBox
                      name="Special Casting Time"
                      value={obj.special_casting_time}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        new_obj.special_casting_time = changed;
                        onChange(new_obj);
                        setRefiltering(true);
                      }}
                    />
                  </Grid> 
                }
                {renderDurations()}
                {renderRanges()}
                <Grid size={{ xs: 6 }}>
                  <SelectStringBox
                    name="Source Type" 
                    options={FEATURE_SOURCE_TYPE_OPTIONS}
                    extra_options={["Any"]}
                    value={obj.source_type} 
                    disabled={disabled}
                    onChange={(changed: DataType) => {
                      const new_obj = obj;
                      new_obj.source_type = changed;
                      onChange(new_obj);
                      setReloading(true);
                      setRefiltering(true);
                    }} 
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {!reloading &&
                    <>
                      {obj.source_type === "Any" ?
                        <SelectObjectBox
                          name="Sources" 
                          data_types={FEATURE_SOURCE_TYPES}
                          values={obj.source_ids} 
                          multiple
                          disabled={disabled}
                          display_format="{obj.feature_source_type_name}: {obj.true_name}"
                          onChange={(changed: string[]) => {
                            const new_obj = obj;
                            new_obj.source_ids = changed;
                            onChange(new_obj);
                            setRefiltering(true);
                          }} 
                        />
                      :
                        <SelectObjectBox
                          name="Sources" 
                          data_type={obj.source_type}
                          values={obj.source_ids} 
                          multiple
                          disabled={disabled}
                          display_format="{obj.true_name}"
                          onChange={(changed: string[]) => {
                            const new_obj = obj;
                            new_obj.source_ids = changed;
                            onChange(new_obj);
                            setRefiltering(true);
                          }} 
                        />
                      }
                    </>
                  }
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SelectStringBox
                    name="In Spellbook"
                    value={obj.in_spellbook}
                    options={["Ignore", "No", "Yes"]}
                    onChange={(changed: "Yes" | "No" | "Ignore") => {
                      const new_obj = obj;
                      new_obj.in_spellbook = changed;
                      onChange(new_obj);
                      setRefiltering(true);
                    }}
                  />
                </Grid>
              </>
            }
          </>
        : mode === "Specific Included Spells" ?
          <>
            <Grid className="header" size={{ xs: 6 }}>
              Specific Included Spells
            </Grid>
            <Grid size={{ xs: 6 }}>
              <ButtonBox
                name="Remove Orphaned"
                onClick={() => {
                  const new_obj = obj;
                  const all_spell_ids = spells.map(o => o._id);
                  new_obj.spell_ids = new_obj.spell_ids.filter(o => all_spell_ids.includes(o));
                  onChange(new_obj);
                  setRefiltering(true);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectSpellIndex
                spell_ids={obj.spell_ids} 
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.spell_ids = changed;
                  onChange(new_obj);
                  setRefiltering(true);
                }} 
              />
            </Grid>
          </>
        : mode === "Specific Excluded Spells" ?
          <>
            <Grid className="header centered" size={{ xs: 12 }}>
              Specific Excluded Spells
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectSpellIndex
                spell_ids={obj.excluded_spell_ids} 
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.excluded_spell_ids = changed;
                  onChange(new_obj);
                  setRefiltering(true);
                }} 
              />
            </Grid>
          </>
        : mode === "Show Spells" ?
          <>
            <DisplayStringWithReplacements display_string={spell_limit_text} />
          </>
        : mode === "Tools" ?
          renderTools()
        : null
        }
      </Grid>
    );
  }
}